import React from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'gatsby-plugin-react-i18next'

import { SEO } from '../../types/page'
import useBrand from '../../hooks/useBrand'
import insertHandlebarsTemplate from '../../utils/insertHandlebarsTemplate'
import useHandlebarsContext from '../../hooks/useHandlebarsContext'

interface Props {
  title?: string
  url: string
  description?: string | null
  imageURL?: string
  imageAlt?: string
  seo?: SEO | null
}

export const OpenGraphSEO: React.FC<Props> = ({
  title,
  description,
  url,
  seo,
  imageURL,
}) => {
  const {
    i18n: { language },
  } = useTranslation()
  const brand = useBrand()
  const handlebarsContext = useHandlebarsContext()
  const openGraphDescription =
    seo?.openGraphDescription ?? seo?.description ?? description
  let openGraphImage = seo?.openGraphImage?.file.url ?? imageURL

  if (openGraphImage && openGraphImage.startsWith('//')) {
    openGraphImage = `https://${openGraphImage}`
  }

  return (
    <Helmet>
      <meta property="og:locale" content={language} />
      <meta
        property="og:title"
        content={seo?.openGraphTitle ?? seo?.pageTitle ?? title}
      />
      {openGraphDescription && (
        <meta
          property="og:description"
          content={insertHandlebarsTemplate(
            openGraphDescription,
            handlebarsContext
          )}
        />
      )}
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={brand.name} />
      <meta property="article:publisher" content={brand.facebook} />
      {openGraphImage && <meta name="og:image" content={openGraphImage} />}
    </Helmet>
  )
}

interface TwitterSEOProps
  extends Pick<Props, 'imageURL' | 'imageAlt' | 'title' | 'description'> {
  data?: Record<string, string>
}

export const TwitterSEO: React.FC<TwitterSEOProps> = ({
  title,
  description,
  imageURL,
  imageAlt,
  data = {},
}) => {
  const brand = useBrand()

  if (!brand.twitter) {
    return null
  }

  return (
    <>
      <Helmet>
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:creator" content={`@${brand.twitter}`} />
        <meta name="twitter:site" content={`@${brand.twitter}`} />
        <meta name="twitter:title" content={title} />
        {description && (
          <meta name="twitter:description" content={description} />
        )}
        {description && (
          <meta name="msapplication-tooltip" content={description} />
        )}

        {imageURL && <meta name="twitter:image" content={imageURL} />}
        {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}
      </Helmet>
      {Object.entries(data).map(([key, value], i) => (
        <Helmet key={key}>
          <meta name={`twitter:label${i + 1}`} content={key} />
          <meta name={`twitter:data${i + 1}`} content={value} />
        </Helmet>
      ))}
    </>
  )
}

export default OpenGraphSEO
